/**
 * Shared Chat Page
 * Public page for viewing shared chat conversations
 * Path: /share/:shareId
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Chat } from '@/components/chat/chat'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { chatService } from '@/services/chat-service'
import { usePageTitle } from '@/hooks/use-page-title'
import type { ChatMessage } from '@/types/chat-message'

/**
 * Agent details interface
 */
interface AgentDetails {
  id: string
  name: string
  description?: string
  published: boolean
  avatar: string | null
}

/**
 * Chat data interface
 */
interface ChatData {
  id: string
  title: string
  agentId?: string
}

/**
 * SharedChatPage Component
 * Displays public read-only view of a shared chat
 */
export function SharedChatPage() {
  const { shareId } = useParams<{ shareId: string }>()

  /**
   * Chat data state
   */
  const [chat, setChat] = useState<ChatData | null>(null)

  /**
   * Agent details state
   */
  const [agent, setAgent] = useState<AgentDetails | null>(null)

  /**
   * Chat messages state
   */
  const [messages, setMessages] = useState<ChatMessage[]>([])

  /**
   * Loading state
   */
  const [loading, setLoading] = useState(true)

  /**
   * Error message state
   */
  const [error, setError] = useState<string | null>(null)

  const pageTitle = usePageTitle(
    chat ? `${chat.title} - Shared Chat` : 'Shared Chat',
    'View shared conversation'
  )

  /**
   * Generates initials from agent name for avatar fallback
   */
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }

  /**
   * Loads shared chat data on mount
   */
  useEffect(() => {
    if (!shareId) {
      setError('Invalid share link')
      setLoading(false)
      return
    }

    const loadSharedChat = async () => {
      try {
        setLoading(true)
        setError(null)

        const [chatResponse, messagesResponse] = await Promise.all([
          chatService.getSharedChat(shareId),
          chatService.getSharedChatMessages(shareId),
        ])

        if (!chatResponse.success || !chatResponse.data) {
          setError('Share link not found or has expired')
          return
        }

        const { chat: chatData, agent: agentData } = chatResponse.data

        setChat(chatData)
        setAgent(agentData)
        setMessages(messagesResponse.success ? messagesResponse.data : [])
      } catch (err) {
        console.error('Failed to load shared chat:', err)
        setError('Failed to load shared chat. The link may be invalid or expired.')
      } finally {
        setLoading(false)
      }
    }

    loadSharedChat()
  }, [shareId])

  /**
   * Renders loading state
   */
  if (loading) {
    return (
      <div className="flex size-full flex-col bg-background">
        {pageTitle}
        <div className="flex size-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-neutral-600">Loading shared chat...</p>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Renders error state
   */
  if (error || !chat) {
    return (
      <div className="flex size-full flex-col bg-background">
        {pageTitle}
        <div className="flex size-full items-center justify-center">
          <div className="text-center max-w-md px-4">
            <h2 className="text-2xl font-semibold mb-2">Chat Not Available</h2>
            <p className="text-neutral-600 mb-6">
              {error || 'This shared chat could not be found.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline">
                <Link to="/signin">Sign In</Link>
              </Button>
              <Button variant="outline">
                <Link to="/signup">Sign Up</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Renders main shared chat view
   */
  return (
    <div className="flex size-full flex-col bg-background">
      {pageTitle}

      <header className="flex items-center justify-between border-b border-neutral-200 px-6 py-3">
        {agent && (
          <div className="flex items-center gap-3">
            <Avatar className="size-10 shrink-0 rounded-xl">
              <AvatarImage
                src={agent.avatar || undefined}
                className="rounded-xl"
              />
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium rounded-xl">
                {getInitials(agent.name)}
              </AvatarFallback>
            </Avatar>
            <h1 className="text-lg font-semibold">{agent.name}</h1>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Link to="/signin">Sign In</Link>
          </Button>
          <Button variant="outline" size="sm">
            <Link to="/signup">Sign Up</Link>
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-hidden">
        <Chat
          key={`shared-${shareId}`}
          id={chat.id}
          title={chat.title}
          initialMessages={messages}
          isReadonly={true}
          agentId={agent?.id}
        />
      </div>
    </div>
  )
}

SharedChatPage.displayName = 'SharedChatPage'
